// ServicesShowcaseSection.js
import React from 'react';
import { ArrowRight } from 'lucide-react';
import alzheimersCareImage from '../assets/alzheimers-care-showcase.jpg';
import personalCareImage from '../assets/personal-care-showcase.jpg';

const ServicesShowcaseSection = () => {
  return (
    <section className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-20">

        {/* Alzheimer's and Dementia Care */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Image */}
          <div>
            <img
              src={alzheimersCareImage}
              alt="An older woman living with dementia at home"
              className="w-full h-[420px] lg:h-[520px] object-cover rounded-2xl"
            />
          </div>

          {/* Right Content */}
          <div>
            <p className="text-primary text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
              Alzheimer's and Dementia Care
            </p>
            <h3 className="font-display font-light text-ink leading-[1.1] text-3xl md:text-4xl mb-6">
              Patient, in-home support for memory care
            </h3>

            <div className="space-y-4 text-muted text-base leading-relaxed mb-8">
              <p>
                Where it is safe to do so, supporting a family member with dementia to stay in their own home can make a real difference. The committed, well-qualified carers at Wei Care support people living with Alzheimer's or dementia in the comfort of their own home. As well as helping them feel safe and secure, which can ease moments of anxiety or confusion, our Alzheimer's and dementia care eases the load on families so they can worry less.
              </p>
              <p>
                Delivered with patience, empathy, and nurturing, our compassionate in-home dementia carers are trained to support clients and families in these ways:
              </p>
            </div>

            <ul className="space-y-3">
              {['Continuity of Care', 'Familiar Frame of Reference', 'Meal Preparation and Assistance', 'Low-Stress Environment'].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full border border-primary text-primary">
                    <ArrowRight size={13} />
                  </span>
                  <span className="text-ink font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Personal Care */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="lg:order-1 order-2">
            <p className="text-primary text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
              Personal Care
            </p>
            <h3 className="font-display font-light text-ink leading-[1.1] text-3xl md:text-4xl mb-6">
              Reliable, dignified help with daily routines
            </h3>

            <div className="space-y-4 text-muted text-base leading-relaxed mb-8">
              <p>
                Most of us begin the day using the bathroom, bathing, dressing, and having something to eat. For many older people this morning routine is harder, as everyday activities of daily living become a struggle. To help, the home care workers at Wei Care provide reliable and discreet personal care for older people.
              </p>
              <p>
                Conveniently available 24 hours a day, 7 days a week, our customised Personal Care solutions include the following:
              </p>
            </div>

            <ul className="space-y-3">
              {['Bathing and Showering', 'Transfer and Ambulation Assistance', 'Toileting and Incontinence Care', 'Oral Hygiene'].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full border border-primary text-primary">
                    <ArrowRight size={13} />
                  </span>
                  <span className="text-ink font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right Image */}
          <div className="lg:order-2 order-1">
            <img
              src={personalCareImage}
              alt="A carer assisting an older woman using a walker"
              className="w-full h-[420px] lg:h-[520px] object-cover rounded-2xl"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesShowcaseSection;
